import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SectionIntro from "./SectionIntro";

// To use real photos for each stop, just swap these paths
// (e.g. drop a Marrakech airport photo in /public/images and point step 1 to it).
const steps = [
  { key: "airport", img: "/images/gallery/marrakech-airport.jpg" },
  { key: "valley", img: "/images/gallery/herov.jpg" },
  { key: "villa", img: "/images/gallery/ext3.jpg" },
];

const GetHere = () => {
  const { t } = useTranslation();

  return (
    <section className="bg-cream py-24 md:py-32 overflow-hidden">
      <div className="max-w-6xl mx-auto px-5 md:px-10">
        <SectionIntro
          eyebrow={t("getHere.eyebrow")}
          title={t("getHere.title")}
          lead={t("getHere.lead")}
          className="mb-16 md:mb-24"
        />

        <div className="relative">
          {/* Center connector line (desktop) */}
          <div className="hidden md:block absolute left-1/2 top-2 bottom-2 w-px bg-ink/12 -translate-x-1/2" />

          <div className="space-y-16 md:space-y-24">
            {steps.map((step, i) => {
              const reversed = i % 2 === 1;
              return (
                <div
                  key={step.key}
                  className={`relative flex flex-col md:flex-row items-center gap-8 md:gap-12 ${
                    reversed ? "md:flex-row-reverse" : ""
                  }`}
                >
                  {/* Image */}
                  <motion.div
                    className="w-full md:w-1/2"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.8 }}
                  >
                    <div className="overflow-hidden aspect-[3/2] bg-cream-dark">
                      <img
                        src={step.img}
                        alt={t(`getHere.steps.${step.key}.title`)}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                  </motion.div>

                  {/* Node */}
                  <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 items-center justify-center">
                    <span className="w-3 h-3 rounded-full bg-brass ring-4 ring-cream" />
                  </div>

                  {/* Text */}
                  <motion.div
                    className={`w-full md:w-1/2 ${
                      reversed ? "md:text-right md:pr-12" : "md:pl-12"
                    }`}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.8, delay: 0.15 }}
                  >
                    <span className="eyebrow text-brass block mb-3">
                      {t(`getHere.steps.${step.key}.label`)}
                    </span>
                    <h3 className="font-serif font-light text-3xl md:text-4xl text-ink leading-tight">
                      {t(`getHere.steps.${step.key}.title`)}
                    </h3>
                    <div className={`rule-gold my-5 ${reversed ? "md:ml-auto" : ""}`} />
                    <p
                      className={`text-ink-soft font-light leading-relaxed max-w-md ${
                        reversed ? "md:ml-auto" : ""
                      }`}
                    >
                      {t(`getHere.steps.${step.key}.desc`)}
                    </p>
                  </motion.div>
                </div>
              );
            })}
          </div>
        </div>

        <motion.div
          className="text-center mt-16 md:mt-20"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <Link
            to="/transfer"
            className="inline-flex items-center justify-center bg-ink hover:bg-brass text-cream text-[12px] uppercase tracking-[0.22em] py-4 px-10 transition-colors duration-300"
          >
            {t("getHere.cta")}
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default GetHere;
